import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Heading from '@atlaskit/heading/heading';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { BannerHeightState } from '@confluence/banners/entry-points/BannerHeightReader.ts';
import { DATABASE_CONTENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';

import DatabaseGraphic from '../icons/DatabaseGraphic.svg';

export type MobileWebViewProps = {
	bannerState: BannerHeightState;
};

type MobileWebViewContainerProps = {
	headerHeight: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MobileWebViewContainer = styled.div<MobileWebViewContainerProps>({
	position: 'absolute',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	top: (props) => `${props.headerHeight}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `calc(100vh - ${props.headerHeight}px)`,
	boxSizing: 'border-box',
	width: '100%',
	padding: `20% ${token('space.250')}`,
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MobileWebViewTextContainer = styled.div({
	font: token('font.body'),
	width: '336px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	textAlign: 'center',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MobileWebViewHeader = styled.p({
	width: '277px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MobileWebViewText = styled.p({
	marginTop: token('space.200'),
});

const i18n = defineMessages({
	title: {
		id: 'confluence-databases.mobile-web-view-title',
		defaultMessage: 'Databases aren’t available on mobile yet',
		description: 'Title displayed on the unsupported mobile web screen',
	},
	description: {
		id: 'confluence-databases.mobile-web-view-description',
		defaultMessage: 'View this on Confluence Cloud for web.',
		description: 'Description displayed on the unsupported mobile web screen',
	},
	details: {
		id: 'confluence-databases.mobile-web-view-details',
		defaultMessage: 'Use databases for managing and syncing your data across Confluence.',
		description: 'Details displayed on the unsupported mobile web screen',
	},
	imageAltText: {
		id: 'confluence-databases.mobile-web-view-img-alt-text',
		defaultMessage: 'Graphic for databases',
		description:
			'The text is used as the alt attribute for an image shown in the mobile view of a Confluence database page. It provides a text description of the decorative graphic displayed to users on mobile devices who cannot access the full database experience.',
	},
});

export const MobileWebView: React.FC<MobileWebViewProps> = ({ bannerState }) => {
	const headerHeight = useMemo(() => bannerState.getTotalHeight(), [bannerState]);
	const intl = useIntl();

	return (
		<MobileWebViewContainer headerHeight={headerHeight}>
			<img src={DatabaseGraphic} alt={intl.formatMessage(i18n.imageAltText)} />
			<MobileWebViewTextContainer>
				<MobileWebViewHeader>
					<Heading size="medium" as="div">
						<FormattedMessage {...i18n.title} />
					</Heading>
				</MobileWebViewHeader>
				<Text>
					<FormattedMessage {...i18n.description} />
				</Text>
				<MobileWebViewText>
					<FormattedMessage {...i18n.details} />
				</MobileWebViewText>
			</MobileWebViewTextContainer>
			<ExperienceSuccess
				name={DATABASE_CONTENT_EXPERIENCE}
				attributes={{ isConfluenceRenderedMobileView: true }}
			/>
		</MobileWebViewContainer>
	);
};
